import { Injectable, signal } from '@angular/core';
import { DateTime } from 'luxon';
import { UserDb } from 'services/user-db.service';
import { DocWatcher } from 'services/database-wrapper';
import { Log_DayStart } from '@cf-shared/data-model/log-day-start';
import { LogRecordType } from '@cf-shared/data-model/user-log';
import { INFOODS } from '@cf-shared/data-model/food';
import { LL } from 'i18n/i18n.service';
import { ProgressChartSeries, ProgressChartDataPoint, ProgressChartAxisConfig } from 'components/cs-progress-chart/cs-progress-chart';

/**
 * Data provider service for nutrition charts.
 * Provides series configuration and data fetching for calorie tracking.
 */
@Injectable({ providedIn: 'root' })
export class ChartDataNutritionService {

  // loading state signal - set to true while fetching data
  isLoading = signal(false);

  constructor(private userDb: UserDb) {}

  /**
   * Get series configuration for nutrition chart.
   * Returns series for logged and target calories.
   */
  getSeries(): ProgressChartSeries[] {
    return [
      {
        id: 'consumed',
        label: LL.nutrition.charts.loggedCalories(),
        colorVar: '--chart-blue',
        measurementTypeId: 'nutrient_kcal',
        precision: 1  // round to whole numbers
      },
      {
        id: 'target',
        label: LL.nutrition.charts.targetCalories(),
        colorVar: '--chart-blue-light',
        dashed: true,
        measurementTypeId: 'nutrient_kcal',
        precision: 1  // round to whole numbers
      }
    ];
  }

  /**
   * Get axis configuration for nutrition chart.
   * Uses single Y-axis for calorie values.
   * The title is translated here and already carries its unit, so no yAxisUnit is set.
   */
  getAxisConfig(): ProgressChartAxisConfig {
    return {
      stacked: false,
      yAxisTitle: LL.progress.axisTitles.caloriesKcal()
    };
  }

  /**
   * Fetch nutrition data for the given date range.
   *
   * @param startDate - ISO date string for range start
   * @param endDate - ISO date string for range end
   * @returns Array of data points with date, consumed, and target calorie values
   */
  async fetchDataAsync(
    startDate: string,
    endDate: string,
    watcher: DocWatcher
  ): Promise<ProgressChartDataPoint[]> {
    this.isLoading.set(true);

    try {
      // convert ISO string dates to DateTime objects
      const startDateTime = DateTime.fromISO(startDate).startOf('day');
      const endDateTime = DateTime.fromISO(endDate).endOf('day');

      // fetch day start log entries in date range
      const logEntries = await this.userDb.getLogEntriesInRangeAsync(
        startDateTime,
        endDateTime,
        watcher,
        { inLogEntriesTimeZone: true }
      );

      const dayStartEntries = logEntries.filter(e => e.type === LogRecordType.DayStart) as Log_DayStart[];

      // extract data points
      const dataPoints: ProgressChartDataPoint[] = [];

      for (const entry of dayStartEntries) {
        const dateISO = DateTime.fromISO(entry.time_utc).toISODate();

        const dataPoint: ProgressChartDataPoint = {
          date: dateISO
        };

        // consumed calories (rounded to whole number)
        if (entry.nutrients && entry.nutrients[INFOODS.Calories] !== undefined) {
          dataPoint['consumed'] = Math.round(entry.nutrients[INFOODS.Calories]);
        }

        // target calories (rounded to whole number)
        if (entry.targets && entry.targets[INFOODS.Calories] && entry.targets[INFOODS.Calories].target !== undefined) {
          dataPoint['target'] = Math.round(entry.targets[INFOODS.Calories].target);
        }

        dataPoints.push(dataPoint);
      }

      // sort by date
      dataPoints.sort((a, b) => a.date.localeCompare(b.date));

      return dataPoints;
    } finally {
      this.isLoading.set(false);
    }
  }
}
